import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { isMobileDevice, withTimeout, logPerformance } from '../utils/mobileOptimization';
import type { User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  impersonatedUserId: string | null;
  effectiveUserId: string | null;
  isImpersonating: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string, company: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  impersonateUser: (userId: string) => Promise<{ error: any }>;
  stopImpersonation: () => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const IMPERSONATION_TOKEN_KEY = 'impersonation_session_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  useEffect(() => {
    const startTime = performance.now();
    const isMobile = isMobileDevice();
    const maxTimeout = isMobile ? 3000 : 5000;

    const timeout = setTimeout(() => {
      console.warn(`[AuthContext] Loading timeout exceeded (${maxTimeout}ms on ${isMobile ? 'mobile' : 'desktop'}), forcing completion`);
      setLoading(false);
    }, maxTimeout);

    let mounted = true;
    let subscription: any = null;

    const initializeAuth = async () => {
      try {
        const sessionPromise = supabase.auth.getSession();
        const { data: { session } } = await withTimeout(
          sessionPromise,
          isMobile ? 2000 : 3000,
          { data: { session: null } }
        );

        if (!mounted) return;

        setUser(session?.user ?? null);

        if (session?.user) {
          const storedToken = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);

          if (storedToken && !isMobile) {
            try {
              await withTimeout(
                verifyAndRestoreImpersonation(storedToken),
                2000
              );
            } catch (error) {
              console.warn('[AuthContext] Impersonation check failed or timed out:', error);
              await cleanupImpersonation();
            }
          }
        } else {
          await cleanupImpersonation();
        }

        clearTimeout(timeout);
        if (mounted) {
          setLoading(false);
          logPerformance('AuthContext initialization', startTime);
        }

        if (!isMobile) {
          try {
            const { data } = supabase.auth.onAuthStateChange((_event, session) => {
              if (!mounted) return;

              (async () => {
                try {
                  setUser(session?.user ?? null);

                  if (!session) {
                    await cleanupImpersonation();
                  }
                } catch (err) {
                  console.warn('[AuthContext] Error in auth state change handler:', err);
                }
              })();
            });

            subscription = data.subscription;
          } catch (error) {
            console.warn('[AuthContext] Auth state listener unavailable (WebSocket error), using manual refresh only');
          }
        } else {
          console.log('[AuthContext] Mobile device detected, skipping realtime subscription');
        }
      } catch (error) {
        console.error('[AuthContext] Error during initialization:', error);
        clearTimeout(timeout);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      clearTimeout(timeout);
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  async function verifyAndRestoreImpersonation(token: string) {
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-impersonation`;
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        await cleanupImpersonation();
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionToken: token }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await cleanupImpersonation();
        return;
      }

      const result = await response.json();

      if (result.success && result.isValid) {
        setSessionToken(token);
        setImpersonatedUserId(result.impersonatedUserId);
      } else {
        await cleanupImpersonation();
      }
    } catch (error) {
      console.error('Error verifying impersonation:', error);
      await cleanupImpersonation();
    }
  }

  async function cleanupImpersonation() {
    const token = sessionStorage.getItem(IMPERSONATION_TOKEN_KEY);

    if (token) {
      try {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/end-impersonation`;
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionToken: token }),
          });
        }
      } catch (error) {
        console.error('Error ending impersonation:', error);
      }
    }

    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    setSessionToken(null);
    setImpersonatedUserId(null);
  }

  const signUp = async (email: string, password: string, firstName: string, lastName: string, company: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          company: company,
        },
      },
    });

    if (!error && data.session) {
      const referralCode = getReferralCodeFromCookie();
      if (referralCode) {
        try {
          const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/attribute-referral`;
          await fetch(apiUrl, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${data.session.access_token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ referral_code: referralCode }),
          });
          document.cookie = 'referral_code=; max-age=0; path=/';
        } catch (e) {
          console.warn('[AuthContext] Failed to attribute referral:', e);
        }
      }
    }

    return { error };
  };

  function getReferralCodeFromCookie(): string | null {
    const match = document.cookie.match(/(?:^|;\s*)referral_code=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Update user immediately for faster UI response
    if (!error && data.session) {
      setUser(data.session.user);
    }

    return { error };
  };

  const signOut = async () => {
    await cleanupImpersonation();
    await supabase.auth.signOut();
  };

  const impersonateUser = async (userId: string): Promise<{ error: any }> => {
    try {
      console.log('[AuthContext] Starting impersonation for userId:', userId);
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/start-impersonation`;
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        console.error('[AuthContext] Not authenticated');
        return { error: { message: 'Not authenticated' } };
      }

      console.log('[AuthContext] Calling start-impersonation API...');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetUserId: userId }),
      });

      console.log('[AuthContext] API response status:', response.status);

      if (!response.ok) {
        const error = await response.json();
        console.error('[AuthContext] API error:', error);
        return { error };
      }

      const result = await response.json();
      console.log('[AuthContext] API result:', result);

      if (result.success && result.sessionToken) {
        console.log('[AuthContext] Impersonation successful, storing token and reloading...');
        sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, result.sessionToken);
        setSessionToken(result.sessionToken);
        setImpersonatedUserId(userId);

        window.location.reload();

        return { error: null };
      }

      console.error('[AuthContext] Invalid result from API:', result);
      return { error: { message: 'Failed to start impersonation' } };
    } catch (error) {
      console.error('[AuthContext] Exception during impersonation:', error);
      return { error };
    }
  };

  const stopImpersonation = async (): Promise<{ error: any }> => {
    try {
      await cleanupImpersonation();

      window.location.reload();

      return { error: null };
    } catch (error) {
      console.error('Error stopping impersonation:', error);
      return { error };
    }
  };

  const effectiveUserId = impersonatedUserId || user?.id || null;
  const isImpersonating = impersonatedUserId !== null;

  return (
    <AuthContext.Provider value={{ user, loading, impersonatedUserId, effectiveUserId, isImpersonating, signUp, signIn, signOut, impersonateUser, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
